var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getAppRoot } from "@/onload/loadConfig";
import { rethrowSimple } from "@/utils/simple-error";
import axios from "axios";
import { CleanableSummary, CleanupResult, } from "./Cleanup/model";
const datasetKeys = "id,name,size,update_time,hda_ldda";
const isDataset = "q=history_content_type-eq&qv=dataset";
const isDeleted = "q=deleted-eq&qv=True";
const isNotPurged = "q=purged-eq&qv=False";
const maxItemsToFetch = 500;
const discardedDatasetsQueryParams = `${isDataset}&${isDeleted}&${isNotPurged}&limit=${maxItemsToFetch}`;
const historyKeys = "id,name,size,update_time";
const discardedHistoriesQueryParams = `&${isDeleted}&${isNotPurged}&limit=${maxItemsToFetch}`;
/**
 * Calculates the total amount of bytes that can be cleaned by permanently removing
 * deleted datasets.
 * @returns Object containing information about how much can be cleaned.
 */
export function fetchDiscardedDatasetsSummary() {
    return __awaiter(this, void 0, void 0, function* () {
        //TODO: possible optimization -> moving this to specific API endpoint so we don't have to parse
        //      potentially a huge number of items
        const summaryKeys = "size";
        const url = `${getAppRoot()}api/datasets?keys=${summaryKeys}&${discardedDatasetsQueryParams}`;
        try {
            const { data } = yield axios.get(url);
            const totalSizeInBytes = data.reduce((partial_sum, item) => partial_sum + item.size, 0);
            return new CleanableSummary({
                totalSize: totalSizeInBytes,
                totalItems: data.length,
            });
        }
        catch (e) {
            rethrowSimple(e);
        }
    });
}
/**
 * Retrieves all deleted datasets of the current user that haven't been purged yet using pagination.
 * @param options Filtering options for pagination and sorting.
 * @returns Array of dataset objects with the fields defined in `datasetKeys` constant.
 */
export function fetchDiscardedDatasets(options = {}) {
    return __awaiter(this, void 0, void 0, function* () {
        let params = "";
        if (options.sortBy) {
            const sortPostfix = options.sortDesc ? "-dsc" : "-asc";
            params += `order=${options.sortBy}${sortPostfix}&`;
        }
        if (options.limit) {
            params += `limit=${options.limit}&`;
        }
        if (options.offset) {
            params += `offset=${options.offset}&`;
        }
        const url = `${getAppRoot()}api/datasets?keys=${datasetKeys}&${discardedDatasetsQueryParams}&${params}`;
        try {
            const { data } = yield axios.get(url);
            return data;
        }
        catch (e) {
            rethrowSimple(e);
        }
    });
}
/**
 * Purges a collection of datasets.
 * @param datasetSourceIds Array of objects with datasets {id, src} to be purged.
 * @returns Result object with `success_count` and `errors`.
 */
export function purgeDatasets(datasetSourceIds) {
    return __awaiter(this, void 0, void 0, function* () {
        const payload = {
            purge: true,
            datasets: datasetSourceIds,
        };
        const url = `${getAppRoot()}api/datasets`;
        try {
            const { data } = yield axios.delete(url, { data: payload });
            return data;
        }
        catch (e) {
            rethrowSimple(e);
        }
    });
}
/**
 * Purges a set of datasets instances (HDA, LDDA, ...) from disk and returns the total space freed in bytes
 * taking into account possible datasets that couldn't be deleted.
 * @param items Array of datasets to be removed from disk.
 *                         Each dataset must contain `id` and `size`.
 * @returns Information about the result of the cleanup operation.
 */
export function cleanupDiscardedDatasets(items) {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        const resultResponse = { errors: [], totalFreeBytes: 0, totalItemCount: 0 };
        try {
            const datasetsTable = items.reduce((acc, item) => ((acc[item.id] = item), acc), {});
            const datasetSourceIds = items.map((item) => {
                const dataset = item;
                return { id: dataset.id, src: dataset.hda_ldda };
            });
            const requestResult = yield purgeDatasets(datasetSourceIds);
            resultResponse.totalItemCount = items.length;
            if (requestResult.errors) {
                resultResponse.errors = mapErrors(datasetsTable, requestResult.errors);
                const erroredIds = (_a = requestResult.errors) === null || _a === void 0 ? void 0 : _a.reduce((acc, error) => [...acc, error.dataset.id], []);
                resultResponse.totalFreeBytes = datasetSourceIds.reduce((partial_sum, item) => partial_sum + ((erroredIds === null || erroredIds === void 0 ? void 0 : erroredIds.includes(item.id)) ? 0 : datasetsTable[item.id].size), 0);
            }
        }
        catch (error) {
            resultResponse.errorMessage = error;
        }
        return new CleanupResult(resultResponse);
    });
}
export function fetchDiscardedHistoriesSummary() {
    return __awaiter(this, void 0, void 0, function* () {
        const summaryKeys = "size";
        const url = `${getAppRoot()}api/histories?keys=${summaryKeys}&${discardedHistoriesQueryParams}`;
        try {
            const { data } = yield axios.get(url);
            const totalSizeInBytes = data.reduce((partial_sum, item) => partial_sum + item.size, 0);
            return new CleanableSummary({
                totalSize: totalSizeInBytes,
                totalItems: data.length,
            });
        }
        catch (e) {
            rethrowSimple(e);
        }
    });
}
export function fetchDiscardedHistories(options = {}) {
    return __awaiter(this, void 0, void 0, function* () {
        let params = "";
        if (options.sortBy) {
            const sortPostfix = options.sortDesc ? "-dsc" : "-asc";
            params += `order=${options.sortBy}${sortPostfix}&`;
        }
        if (options.limit) {
            params += `limit=${options.limit}&`;
        }
        if (options.offset) {
            params += `offset=${options.offset}&`;
        }
        const url = `${getAppRoot()}api/histories?keys=${historyKeys}&${discardedHistoriesQueryParams}&${params}`;
        try {
            const { data } = yield axios.get(url);
            return data;
        }
        catch (e) {
            rethrowSimple(e);
        }
    });
}
function purgeHistory(historyId) {
    return __awaiter(this, void 0, void 0, function* () {
        const payload = {
            purge: true,
        };
        const url = `${getAppRoot()}api/histories/${historyId}`;
        try {
            const { data } = yield axios.delete(url, { data: payload });
            return data;
        }
        catch (e) {
            rethrowSimple(e);
        }
    });
}
export function cleanupDiscardedHistories(histories) {
    return __awaiter(this, void 0, void 0, function* () {
        const resultResponse = { errors: [], totalFreeBytes: 0, totalItemCount: 0 };
        const historiesTable = histories.reduce((acc, item) => ((acc[item.id] = item), acc), {});
        // TODO: Promise.all() and do this in parallel?  Or add a bulk delete endpoint?
        try {
            for (const history of histories) {
                yield purgeHistory(history.id);
                resultResponse.totalFreeBytes += historiesTable[history.id].size;
                resultResponse.totalItemCount += 1;
            }
        }
        catch (error) {
            resultResponse.errorMessage = error;
        }
        return new CleanupResult(resultResponse);
    });
}
/**
 * Maps the error messages with the dataset name for user display.
 * @param datasetsTable Datasets dictionary indexed by ID
 * @param errors List of errors associated with each dataset ID
 * @returns A list with the name of the dataset and the associated error message.
 */
function mapErrors(datasetsTable, errors) {
    return errors.map((error) => {
        const name = datasetsTable[error.dataset.id].name;
        return { name: name, reason: error.error_message };
    });
}
